<template>
  <div :class="jel_getClassObject(content)">
    <div v-if="isModeInscription" class="ml-n2">
      <ul>
        <li
          v-for="(item, idx) in rules.rules[ruleType]"
          :key="idx"
          :class="{
            texteInit: !validated,
            texteValide: motDePasse && validatePwdCondition(item.regex),
            texteInvalide: validated && !validatePwdCondition(item.regex),
          }"
        >
          {{ item.text }}
        </li>
      </ul>
    </div>
    <!-- tag="div" nécessaire pour accessibilité, éviter div dans un span -->
    <ValidationProvider
      v-slot="validationContext"
      ref="observer"
      :name="content.libMotDePasse"
      :rules="regleValidation"
      tag="div"
    >
      <!-- On passe par un hidden field pour faire afficher erreurs de valiation -->
      <!-- Ceci est nécessaire car le component Password n'offre par un input field au premier niveau -->
      <b-form-group :label="content.libMotDePasse" label-for="motDePasse" class="mb-1">
        <div class="d-flex">
          <b-form-input
            :id="content.idHTML ? content.idHTML : 'motDePasse'"
            v-model="motDePasse"
            :name="content.nameHTML"
            autocomplete="off"
            :type="passwordInputType"
            :maxlength="rules.maxPwdLength"
            :state="getValidationState(validationContext)"
            @input="afficherMessageForce()"
          ></b-form-input>
          <div
            v-if="isModeInscription"
            class="d-flex justify-content-center flex-column icone-mdp-conteneur"
            @click="toggleHiddenMdp()"
          >
            <span
              v-if="contenuTooltip"
              aria-hidden="true"
              class="password-icon-tooltip text-center"
            >
              {{ contenuTooltip }}
            </span>
            <svg-icon className="icone-mdp" :class="iconeOeil" :icon-name="iconeOeil"></svg-icon>
            <label class="sr-only">{{ $t('compte.mdp.libelleAfficherMdp') }}</label>
          </div>
        </div>
        <password
          :id="content.idHTML ? content.idHTML : 'motDePasse'"
          v-model="motDePasse"
          :name="content.nameHTML"
          defaultClass="form-control"
          :secureLength="rules.minPwdLength"
          :strength-meter-only="true"
          :showStrengthMeter="false"
          :toggle="true"
          :badge="false"
          :maxlength="rules.maxPwdLength"
          @score="updateScore"
          @xblur="$refs.observer.validate()"
          @blur="forceValidate()"
        />
      </b-form-group>
      <div v-if="isModeInscription">
        <ErreurValidation
          v-show="
            validationContext.errors[0] === rules.rules['mdpCaracteresSpeciaux'][0].messageErreur ||
            validationContext.errors[0] === erreurChampObligatoire.message
          "
          :message="validationContext.errors[0]"
          classesCSS="pb-3"
        ></ErreurValidation>
        <div
          v-if="
            validated &&
            validationContext.errors[0] !== rules.rules['mdpCaracteresSpeciaux'][0].messageErreur &&
            validationContext.errors[0] !== erreurChampObligatoire.message
          "
        >
          {{ $t('messageComponent.forceMdp.message') }}
          <span class="font-weight-bold">{{ texteScore }}</span>
        </div>
      </div>
      <div v-else>
        <ErreurValidation
          :message="validationContext.errors[0]"
          classesCSS="pb-3"
        ></ErreurValidation>
        <div v-if="content.lien" class="lienPrimaire">
          <Hyperlien :content="motDePasseOublie" class="petitTexte"></Hyperlien>
        </div>
      </div>
    </ValidationProvider>
  </div>
</template>

<script>
import Password from 'vue-password-strength-meter';
import calculateurForceMotDePasse from 'zxcvbn';
import Hyperlien from '@/components/base/Hyperlien';
// https://www.npmjs.com/package/vue-password-strength-meter
import rules from '@/js/validators/rules/compte';
import ErreurValidation from '@/components/formulaire/compte/ErreurValidation';

import mixinMessageErreur from '@/mixins/mixinMessageErreur';

export default {
  name: 'FormulaireMotDePasse',

  components: { Password, Hyperlien, ErreurValidation },
  mixins: [mixinMessageErreur],
  props: { content: { type: Object, required: true }, value: { type: String, required: false } },
  data() {
    return {
      rules,
      ruleType: 'motDePasse',
      mdpHidden: true,
      mdpScore: 0,
      validated: false,
      erreurChampObligatoire: {
        id: 'champ-obligatoire',
        message: this.$i18n.t('validations.messages.required'),
        montrerMessage: false,
      },
    };
  },
  i18n: {
    messages: {
      fr: {
        messageComponent: {
          forceMdp: {
            message: 'Sécurité du mot de passe : ',
            faible: 'faible',
            moyen: 'moyenne',
            fort: 'forte',
          },
        },
      },
      en: {
        messageComponent: {
          forceMdp: {
            message: 'Password strength: ',
            faible: 'Weak',
            moyen: 'Average',
            fort: 'Strong',
          },
        },
      },
    },
  },
  computed: {
    motDePasse: {
      get() {
        // Sans "v-model" et donc via interface Magnolia
        if (this.value == null) {
          return this.$store.state.compte.motDePasse;
        }
        return this.value;
      },
      set(value) {
        // Sans "v-model" et donc via interface Magnolia
        if (this.value == null) {
          this.$store.commit('compte/SET_MOT_DE_PASSE', value);
        } else {
          this.$emit('input', value);
        }
      },
    },
    iconeOeil() {
      return this.mdpHidden ? 'icone-mdp-cache' : 'icone-mdp-affiche';
    },
    contenuTooltip() {
      return this.mdpHidden ? this.content.txtBulleAfficherMdp : this.content.txtBulleCacherMdp;
    },
    isModeInscription() {
      return this.content.modeInscription == 'inscription';
    },
    motDePasseOublie() {
      return {
        texte: this.content.libMotDePasseOublie,
        lien: this.content.lienMotDePasseOublie,
        type: 'lienPrimaire',
      };
    },
    passwordInputType() {
      return this.mdpHidden ? 'password' : 'text';
    },
    regleValidation() {
      return this.isModeInscription ? 'required|compteValidation:mdpCaracteresSpeciaux' : '';
    },
    texteScore() {
      return this.getForceMdp();
    },
  },
  created() {
    if (!this.isModeInscription) {
      this.$store.state.compte.motDePasse = '';
    }
  },
  mounted() {
    this.afficherMessageForce();
    this.mixinMessageErreur_getMessage(
      this.erreurChampObligatoire.message,
      null,
      this.erreurChampObligatoire.id
    );
  },
  methods: {
    getForceMdp() {
      if (this.mdpScore <= 1) {
        return this.$i18n.t('messageComponent.forceMdp.faible');
      }
      if (this.mdpScore == 2 || this.mdpScore == 3) {
        return this.$i18n.t('messageComponent.forceMdp.moyen');
      }
      return this.$i18n.t('messageComponent.forceMdp.fort');
    },
    afficherMessageForce() {
      if (this.isModeInscription) {
        if (this.motDePasse.length > 0) {
          this.validated = true;
        }
        this.mdpScore = calculateurForceMotDePasse(this.motDePasse).score;
      }
    },
    toggleHiddenMdp() {
      this.mdpHidden = !this.mdpHidden;
    },
    validatePwdCondition(regex) {
      return rules.validateCompteCondition(this.motDePasse, regex);
    },
    forceValidate() {
      this.$refs.observer.validate();
    },
    updateScore(score) {
      this.mdpScore = score;
    },
    getValidationState({ dirty, validated, valid = null }) {
      // Uniquement si la validation a été effectuée une fois et que le champ est invalide
      if (validated && !valid) {
        return valid;
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
.icone-mdp-conteneur .icone-mdp {
  width: 3rem;
  height: 36px;
  border-radius: 0.25rem;

  fill: #5c677d;
  stroke: none;
  color: none;

  &:hover {
    background-color: $couleur-body-panel-noir;
  }
}

.icone-mdp-cache {
  padding: 11px;
}

.icone-mdp-affiche {
  padding: 10px;
}

.icone-mdp-conteneur {
  width: 0;
  position: relative;
  right: 49px;
  cursor: pointer;
}

.password-icon-tooltip {
  pointer-events: none;
  position: absolute;
  bottom: 100%;
  left: -14px;
  padding: 8px;
  color: #f1f2f3;
  background-color: #000;
  font-size: 12px;
  border-radius: 3px;
  display: none;

  &:before {
    content: '';
    position: absolute;
    top: 100%;
    transform: translateX(-50%);
    left: 50%;
    width: 0;
    height: 0;
    border-color: #000 transparent transparent;
    border-style: solid;
    border-width: 8px 8px 0;
  }
  &:has(+ .icone-mdp:hover) {
    display: block;
  }
}

.invalid-char {
  color: red;
}

@media screen and (max-width: 767px) {
  .icone-mdp-conteneur .icone-mdp {
    height: 46px;
  }
}
</style>
